import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Target, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtEUR, fmtPct, nowPeriod } from "@/lib/format";
import { ChannelBadge, ConfirmDialog, EmptyState, Hint, Loading, PageHeader, PeriodSelect, btnPrimary } from "@/components/app/common";
import { BudgetBar, STATO } from "@/components/app/BudgetAlerts";
import { BudgetDialog } from "@/components/app/BudgetDialog";

const Summary = ({ r }) => (
  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
    {[["Budget totale", fmtEUR(r.budget_totale), "budget-totale"], ["Speso", fmtEUR(r.speso_totale), "budget-speso"],
      ["In linea", r.n_ok, "budget-n-ok"], ["Attenzione", r.n_attenzione, "budget-n-attenzione"], ["Sforati", r.n_sforato, "budget-n-sforato"]].map(([l, v, t]) => (
      <div key={t} className="card-soft px-5 py-4" data-testid={t}>
        <div className="text-[11px] uppercase tracking-wider text-[#756B64] font-semibold">{l}</div>
        <div className="text-xl font-semibold tabular mt-1">{v}</div>
      </div>
    ))}
  </div>
);

function BudgetRow({ b, i, onEdit, onDelete }) {
  const s = STATO[b.stato];
  return (
    <div data-testid={`budget-row-${i}`} className="card-soft p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{b.categoria}</span>
          {b.canale ? <ChannelBadge canale={b.canale} /> : <span className="text-[11px] text-[#756B64]">Tutti i canali</span>}
        </div>
        <div className="flex items-center gap-2">
          <span data-testid={`budget-stato-${i}`} className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${s.badge}`}>{s.label}</span>
          <button data-testid={`edit-budget-${i}`} onClick={() => onEdit(b)} className="p-2 rounded-lg hover:bg-[#F4EFEA] text-[#756B64]"><Pencil className="h-4 w-4" /></button>
          <button data-testid={`delete-budget-${i}`} onClick={() => onDelete(b)} className="p-2 rounded-lg hover:bg-[#E07A5F]/10 text-[#B4553D]"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <BudgetBar pct={b.percentuale} stato={b.stato} />
      <div className="flex flex-wrap justify-between gap-2 mt-2 text-xs text-[#756B64] tabular">
        <span><b className="text-[#2C2623] font-semibold" data-testid={`budget-speso-${i}`}>{fmtEUR(b.speso)}</b> spesi su {fmtEUR(b.importo)}</span>
        <span data-testid={`budget-pct-${i}`}>{fmtPct(b.percentuale)} · {b.residuo >= 0 ? `restano ${fmtEUR(b.residuo)}` : `oltre di ${fmtEUR(-b.residuo)}`}</span>
      </div>
    </div>
  );
}

export default function Budget() {
  const [period, setPeriod] = useState(nowPeriod());
  const [data, setData] = useState(null);
  const [categorie, setCategorie] = useState([]);
  const [edit, setEdit] = useState({ open: false, item: null });
  const [del, setDel] = useState(null);

  const load = useCallback(() => api.get("/budget/stato", { params: period }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [period]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categorie", { params: { tipo: "costo" } }).then((r) => setCategorie(r.data)); }, []);

  const remove = async () => {
    try { await api.delete(`/budget/${del.id}`); toast.success("Budget eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  return (
    <div data-testid="budget-page">
      <PageHeader overline="Pianificazione" title="Budget mensili" subtitle="Imposta un tetto di spesa mensile per categoria (ed eventualmente per canale) e controlla a che punto sei.">
        <PeriodSelect anno={period.anno} mese={period.mese} onChange={(p) => setPeriod({ ...p, mese: p.mese || 1 })} prefix="budget" />
        <Button className={btnPrimary} onClick={() => setEdit({ open: true, item: null })} data-testid="add-budget-button"><Plus className="h-4 w-4 mr-1" />Nuovo budget</Button>
      </PageHeader>
      <div className="mb-6"><Hint testId="budget-hint">Il budget vale per ogni mese. Stato: <b>In linea</b> sotto l'80%, <b>Attenzione</b> dall'80% al 100%, <b>Sforato</b> oltre il 100% della spesa prevista (imponibile).</Hint></div>
      {!data ? <Loading /> : !data.items.length ? (
        <EmptyState icon={Target} title="Nessun budget impostato" text="Crea il primo budget per tenere sotto controllo le spese." action={<Button className={btnPrimary} onClick={() => setEdit({ open: true, item: null })}>Crea budget</Button>} />
      ) : (
        <>
          <Summary r={data.riepilogo} />
          <div className="grid lg:grid-cols-2 gap-4" data-testid="budget-list">
            {data.items.map((b, i) => <BudgetRow key={b.id} b={b} i={i} onEdit={(x) => setEdit({ open: true, item: x })} onDelete={setDel} />)}
          </div>
        </>
      )}
      <BudgetDialog open={edit.open} item={edit.item} categorie={categorie} onOpenChange={(o) => setEdit((s) => ({ ...s, open: o }))} onSaved={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare il budget?" text={del ? `${del.categoria}${del.canale ? ` · ${del.canale}` : ""}` : ""} onConfirm={remove} />
    </div>
  );
}
